import { Injectable } from '@angular/core';
import { from, pipe, of } from 'rxjs';
import { catchError, distinct, filter, map, mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { ExternalContactApiService } from '../services/external-contact-api.service';
import {
  loadContact,
  contactLoaded,
  updateContact,
  contactUpdated,
  subscribeToExternalContactNotifications,
  contactLoadFailed
} from '../actions/external-contact.actions';
import { ConversationBase } from 'common/store/genesys-cloud/conversations/common/models/conversation-base.model';
import { getConversationChangedActions$, getConversationLoadedActions$ } from 'common/store/genesys-cloud/conversations/common/helpers/merged-actions';
import { selectUserHasPermission } from '../../users/selectors/permission.selectors';
import { UserPermission } from '../../users/models/user-permission.enum';

@Injectable()
export class GenesysCloudExternalContactEffects {
  private readonly mapCustomerParticipantToLoadContactAction = pipe(
    concatLatestFrom((_: ConversationBase) => this.store.select(selectUserHasPermission(UserPermission.ExternalContactView))),
    filter(([_, hasPermission]) => hasPermission),
    map(([conversation, _]) => conversation.customerParticipant?.externalContactId),
    filter(contactId => !!contactId),
    distinct(),
    map(contactId => loadContact({ contactId }))
  );

  readonly listenOnConversationsLoaded$ = createEffect(() => getConversationLoadedActions$(this.actions$).pipe(
    mergeMap(({ conversations }) => from(conversations)),
    this.mapCustomerParticipantToLoadContactAction
  ));

  readonly listenOnConversationChanges$ = createEffect(() => getConversationChangedActions$(this.actions$).pipe(
    map(({ conversation }) => conversation),
    this.mapCustomerParticipantToLoadContactAction
  ));

  readonly listenOnContactLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(contactLoaded),
    map(({ contact }) => subscribeToExternalContactNotifications({ contactId: contact.id }))
  ));

  readonly loadContact$ = createEffect(() => this.actions$.pipe(
    ofType(loadContact),
    map(({ contactId }) => contactId),
    distinct(),
    mergeMap(contactId =>
      this.externalContactApi.getContact$(contactId).pipe(
          map((contact) => contactLoaded({ contact })),
          catchError(() => of(contactLoadFailed()))
        )
      )
  ));

  readonly updateContact$ = createEffect(() => this.actions$.pipe(
    ofType(updateContact),
    mergeMap(({ contactId }) => this.externalContactApi.getContact$(contactId)),
    map(contact => contactUpdated({ contact }))
  ));

  constructor(private readonly actions$: Actions,
              private readonly externalContactApi: ExternalContactApiService,
              private readonly store: Store
  ) {
  }
}
